import type { ImageKind } from '../../data/imageTypes';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, ChevronsLeft, ChevronsRight, Info, Loader2, Maximize2, Minimize2, PenLine } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { isLargeNow, useIsLarge } from '../../hooks/useMediaQuery';
import { useSeries } from '../../hooks/useSeries';
import { useSeriesMetadata } from '../../hooks/useSeriesMetadata';
import { seriesDisplayName } from '../../data/types';
import {
  Tag,
  formatDicomDate,
  formatDicomTimeShort,
  formatPersonName,
  getPersonName,
  getString,
  patientAge,
} from '../../data/dicomTags';
import { useHeaderStore } from '../../stores/headerStore';
import { useColorStore } from '../../stores/colorStore';
import { useAnnotations } from '../../hooks/useAnnotations';
import { Viewport } from '../../viewer/Viewport';
import { preloadEngine, sopClassOf } from '../../viewer/engineSelector';
import { SopClass } from '../../viewer/sopClasses';
import { DocumentView, type DocumentControls } from '../document/DocumentView';
import {
  slideTools,
  type EngineCapabilities,
  type ViewportEngine,
  type ViewState as EngineViewState,
} from '../../viewer/ViewportEngine';
import { uidSuffix } from '../share/api';
import { getStudyRef, lookupStudy } from '../collections/api';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { getConfig } from '../../config/runtimeConfig';
import { getDataSource } from '../../data/dataSourceFactory';
import { ImageNavigator } from './navigator/ImageNavigator';
import { groupsBySpecimen, navigatorKey, useMarkSeen, useNavigator, useReorder, useResetSeen, type NavigatorImage } from './navigator/api';
import { displayName, inDisplayOrder, isTail, orderByName, reorder as reorderImages, sortSeries, withSeriesTail, type Neighbours } from './navigator/sections';
import { comparableSeries, expectedSlide, firstPanes, gridPaneCount, initialSlide, resolvePanes } from './openSlide';
import { onScreenSeries, useSeenMarker } from './seen';
import { LabelImage } from './LabelImage';
import { PANEL_DEFAULT_WIDTH, RAIL_WIDTH, clampDockShare, clampPanelWidth, useRightPanel } from './rightPanel';
import { MetadataPanel } from './MetadataPanel';
import { StudyHeader, type AnnotationToolProps, type ShapeKind } from './StudyHeader';
import type { AnnotationTool } from './AnnotationOverlay';
import { SlideAnnotations } from './SlideAnnotations';
import { AnnotationsPanel } from './AnnotationsPanel';
import { useCounterSession } from './useCounterSession';
import { TabVisibility } from './tabVisibility';
import { counterColorIndex } from '../../annotation/style';
import { CompareView, type CompareLayout } from './CompareView';
import type { Annotation } from '../../annotation/types';
import { polygonBounds } from '../../annotation/geometry';
import { AnnotationWriteError } from '../../annotation/AnnotationStore';
import { annotationAccess } from './annotationAccess';
import { editDetailsAction } from './detailsAccess';
import { useReviewScan } from './useReviewScan';
import { useCapture } from '../capture/useCapture';
import { useWindowLevels } from './useWindowLevel';
import { WindowControl } from './WindowControl';
import { WindowOverlay } from './WindowBox';
import { deriveSeriesInfo } from '../../hooks/useStudyThumbnail';

// The snapshot dialog and the drawing behind it, fetched the first time a rectangle is drawn.
const CaptureDialog = lazy(() => import('../capture/CaptureDialog').then((m) => ({ default: m.CaptureDialog })));

/** URL slot per pane, in pane order — ?a=&b=&c=&d=. */
const PANE_PARAMS = ['a', 'b', 'c', 'd'] as const;
/** What each pane is called in the UI. */
const PANE_LABELS = ['A', 'B', 'C', 'D'] as const;

function Spinner({ className }: { className?: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <Loader2 className={className ?? 'h-6 w-6 animate-spin text-muted-foreground'} />
    </div>
  );
}

function ErrorBox({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex h-full items-center justify-center p-4">
      <div className="max-w-lg rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        <div className="mb-1 font-semibold">{title}</div>
        {message}
      </div>
    </div>
  );
}

/**
 * What a right-hand tab is told about the image it sits beside. `engine` is
 * the slide's while the image is open in the single view, and null otherwise
 * (compare, a slide still opening): it always draws the image `seriesUID`
 * names.
 */
export interface StudyTabContext {
  engine: ViewportEngine | null;
  /** Why `engine` is null, in words, when the reason is known (compare). */
  noEngineReason?: string;
  studyUID: string | undefined;
  /** The study's public id (`stu_…`). */
  studyId: string | undefined;
  /** The image the right-hand panels reflect: the active pane in compare. */
  seriesUID: string | undefined;
  /** Its public id (`img_…`); undefined for a series the navigator does not know. */
  imageId: string | undefined;
  imageKind: ImageKind | undefined;
  /** As the page reads it: undefined until it knows. */
  readOnly: boolean | undefined;
}

/** A right-hand tab between Annotations and Info. */
export interface StudyPageTab {
  id: string;
  /** What the tab shows: words, or words with an icon. */
  label: ReactNode;
  /** The tab in plain words, for the folded panel's tooltip; the id when not given. */
  title?: string;
  /** The tab on the folded panel's rail; its title's first letters when not given. */
  icon?: ReactNode;
  /**
   * Whether the tab is shown at all, as a hook the page runs for it (in a
   * component of its own, tabVisibility.tsx), so the rule may ask the server.
   * Absent, the tab is always there; present, the tab is out until the hook
   * first says yes, and `undefined` (no answer yet, a list still loading)
   * keeps the last answer, so a tab does not blink out on every image change.
   * The list a shell passes must be fixed for the page.
   */
  useVisible?: (ctx: StudyTabContext) => boolean | undefined;
  render: (ctx: StudyTabContext) => ReactNode;
}

/**
 * What a shell docks under the tabs (the Discussion): open at a share of the
 * panel's height, folded to its header, or taking the whole panel, as the
 * person leaves it. On a phone it is one more tab instead.
 */
export interface StudyPageDock {
  id: string;
  label: string;
  icon: ReactNode;
  render: (ctx: StudyTabContext) => ReactNode;
  /** A count beside the label and on the rail: renders the number, or nothing. */
  Count?: ComponentType<{ ctx: StudyTabContext }>;
}

/**
 * What a shell may add to the study page without forking it: a bar above
 * the viewer (the community breadcrumb with "Upload"), a bar below it
 * (the community theme switch and color management), right-hand tabs between
 * Annotations and Info (AI in both shells), and the dock under them (the
 * Discussion, in both shells).
 */
export interface StudyPageExtras {
  topBar?: ReactNode;
  bottomBar?: ReactNode;
  /** In the order they are shown, between Annotations and Info. */
  extraTabs?: StudyPageTab[];
  dock?: StudyPageDock;
  /** The study, when it is not in the route (a share link names it by suffix). */
  studyUID?: string;
  /**
   * The study's public id (`stu_…`), which annotations are filed under. The
   * community pages have it from the study head; without it (the enterprise
   * worklist opens a study by UID) it is looked up.
   */
  studyId?: string;
  /**
   * A share link keeps the image on screen as a short suffix in its own path
   * (`/s/stu/<code>/<slide>`) instead of `?series=`: the slide to open first,
   * and where to report the one on screen.
   */
  slide?: string;
  onSlide?: (seriesUid: string) => void;
  /**
   * Whether the person may only VIEW annotations here: a workspace viewer, or
   * anyone reading another person's study (a Public one, or through a share
   * link). The community pages know it from the study head. Left undefined
   * (enterprise), the page asks the head itself.
   */
  readOnly?: boolean;
  /**
   * How the shell tells the person something went wrong: the community toast.
   * Without it (enterprise has no toast) the page shows its own notice.
   */
  notify?: (message: string) => void;
  /**
   * Opens the study's details editor, by public id. Offered in the header only
   * once the page knows the person may write the study (`readOnly` false).
   * The enterprise route passes it; the community bar has its own entry.
   */
  onEditDetails?: (studyId: string) => void;
  /**
   * Where the navigator (the image list) and the images' previews are read:
   * the API root, or — through a share link — the link's namespace under it.
   * Left undefined (enterprise), the API root.
   */
  navigatorBase?: string;
  /** An image's preview by public id, when the shell has its own way (the community's fixture mode). */
  imageThumbnail?: (imageId: string) => string;
}

export function StudyPage({ topBar, bottomBar, extraTabs, dock, studyUID: studyProp, studyId: studyIdProp, slide, onSlide, readOnly: readOnlyProp, notify, onEditDetails, navigatorBase, imageThumbnail }: StudyPageExtras = {}) {
  const routeParams = useParams<{ studyUID: string }>();
  const studyUID = studyProp ?? routeParams.studyUID;
  // The viewer's code, fetched alongside the study's lists rather than after its first slide's metadata.
  useEffect(preloadEngine, []);
  const { data: listed, isLoading: seriesLoading, error: seriesError } = useSeries(studyUID);
  // Reached by UID (the enterprise worklist): the public id is asked for in the
  // workspace the header selected — the one the enterprise shell binds DICOMweb
  // to (AppLayout), so the slide and its annotations are the same study. A study
  // it cannot name (a 404) simply has no annotations here.
  const { current: workspace, ready: workspaceReady } = useSelectedWorkspace(!studyIdProp);
  const lookup = useQuery({
    queryKey: ['study-lookup', workspace?.public_id, studyUID],
    queryFn: () => lookupStudy(studyUID!, workspace?.public_id),
    enabled: !studyIdProp && !!studyUID && workspaceReady,
    retry: false,
  });
  const studyId = studyIdProp ?? lookup.data?.public_id;
  // The navigator: the published images in their saved order, named, typed
  // and grouped, and whether this person may reorder them. It never fails
  // (an empty answer where there is none to read), so "settled" is having
  // been fetched — or, with no public id to ask by, the lookup having given
  // up: a disabled query would otherwise report pending for ever.
  const base = navigatorBase ?? getConfig().apiRoot;
  const navigator = useNavigator(studyUID, studyId, base);
  const navigatorSettled = navigator.isFetched || navigator.isError || (!studyId && lookup.isError);
  // The list this page opened with (decided 2026-09-30): the ids of the first
  // list it had settled — the cache's, or the fetch that landed — for this
  // study. An image published after that is "later": shown after the others,
  // by name among them, until the next load sorts it in (orderByName).
  const [opened, setOpened] = useState<{ study: string; ids: ReadonlySet<string> } | null>(null);
  useEffect(() => {
    if (!studyUID || !navigator.data || navigator.isFetching || opened?.study === studyUID) return;
    setOpened({ study: studyUID, ids: new Set(navigator.data.images.map((img) => img.image_id)) });
  }, [studyUID, navigator.data, navigator.isFetching, opened]);
  const later = useMemo(() => {
    if (!navigator.data || !opened || opened.study !== studyUID) return undefined;
    const ids = navigator.data.images.filter((img) => !opened.ids.has(img.image_id)).map((img) => img.image_id);
    return ids.length ? new Set(ids) : undefined;
  }, [navigator.data, opened, studyUID]);
  // The images as the navigator shows them, top to bottom — sections, then
  // groups, then the saved order where somebody placed the study, else by
  // name — each numbered by its place in its group: the first one is the one
  // a study opens on, "3 of 7" the third card of a group of seven.
  const specimen = groupsBySpecimen();
  const images = useMemo(() => {
    const nav = navigator.data;
    return inDisplayOrder(nav ? (nav.placed ? nav.images : orderByName(nav.images, later)) : [], specimen);
  }, [navigator.data, later, specimen]);
  // QIDO's list in that order; what QIDO lists and the navigator does not
  // (for a moment after a publish, or where there is no navigator) keeps its
  // own order at the end, and is shown as "Other".
  const series = useMemo(() => (listed ? sortSeries(listed, images) : undefined), [listed, images]);
  const shownImages = useMemo(() => withSeriesTail(images, series ?? []), [images, series]);
  // Named and numbered as the cards are: from what the list shows (QIDO can
  // lag the navigator for a moment after a publish), not from every image.
  const byUid = useMemo(
    () => new Map(inDisplayOrder(shownImages, specimen).map((img) => [img.series_instance_uid, img])),
    [shownImages, specimen],
  );
  // Who may annotate, when the shell did not say: the study head's `editable`
  // (an admin or editor of the study's workspace), the same answer the
  // community pages read. Until it answers, and if it cannot, nothing is
  // editable and the tools stay hidden.
  const rights = useQuery({
    queryKey: ['study-ref', studyId],
    queryFn: () => getStudyRef(studyId!),
    enabled: readOnlyProp === undefined && !!studyId,
    retry: false,
  });
  // Set when the server refuses an annotation write as forbidden: its answer
  // outranks whatever the page was told when it loaded. Per study.
  const [denied, setDenied] = useState(false);
  useEffect(() => setDenied(false), [studyId]);
  const readOnly: boolean | undefined = denied
    ? true
    : readOnlyProp ?? (rights.isSuccess ? !rights.data.editable : undefined);
  const editDetails = editDetailsAction(onEditDetails, studyId, readOnly);
  const [notice, setNotice] = useState<string | null>(null);

  const [params, setParams] = useSearchParams();
  const [selectedSeriesUID, setSelectedSeriesUID] = useState<string>();
  const [viewportKey, setViewportKey] = useState(0);

  // Single-mode viewport handles, for the header's view actions.
  const engineRef = useRef<ViewportEngine | null>(null);
  const [engine, setEngine] = useState<ViewportEngine | null>(null);
  // The series that engine draws: a slide being swapped holds the old one for a moment.
  const [engineSeries, setEngineSeries] = useState<string | undefined>(undefined);
  const [caps, setCaps] = useState<EngineCapabilities | null>(null);
  // Compare's panes' engines, each with the slide it draws: a pane whose slide
  // is swapped still holds the old one's until that one is torn down.
  const [paneEngines, setPaneEngines] = useState<({ engine: ViewportEngine; series: string } | null)[]>([]);
  const onPaneEngine = useCallback((pane: number, e: ViewportEngine | null, series: string | undefined) => {
    setPaneEngines((prev) => {
      const next = prev.slice();
      next[pane] = e && series ? { engine: e, series } : null;
      return next;
    });
  }, []);
  const [tool, setTool] = useState<AnnotationTool>('none');
  // Phones and portrait tablets (under 1024px): the image list and the side
  // panel open over the image, one at a time, instead of squeezing it.
  const [mobilePanel, setMobilePanel] = useState<'images' | 'details' | null>(null);
  // Polygon draw mode (smooth spline vs straight), remembered per user.
  const [polygonSmooth, setPolygonSmooth] = useState<boolean>(() => {
    try {
      return localStorage.getItem('viewer:polygonSmooth') !== '0';
    } catch {
      return true;
    }
  });
  // Last-used shape for the shapes-group button (shown when nothing is armed).
  const [lastShape, setLastShape] = useState<ShapeKind>(() => {
    try {
      const v = localStorage.getItem('viewer:lastShape');
      if (v === 'rectangle' || v === 'ellipse' || v === 'polygon-straight' || v === 'polygon-smooth') {
        return v;
      }
    } catch {
      /* ignore */
    }
    return 'rectangle';
  });
  // 'annotations', 'information', or an extra tab's id.
  const [rightTab, setRightTab] = useState<string>('annotations');
  // What the shell's gated tabs answered (StudyPageTab.useVisible); a gated
  // tab not yet answered is out.
  const [tabVisible, setTabVisible] = useState<Record<string, boolean>>({});
  const onTabVisible = useCallback((id: string, visible: boolean) => {
    setTabVisible((v) => (v[id] === visible ? v : { ...v, [id]: visible }));
  }, []);
  // The panel's shape, as this browser remembers it (rightPanel.ts). Only the
  // wide layout folds, docks and resizes; on a phone the panel is a sheet.
  const large = useIsLarge();
  const panelOpen = useRightPanel((st) => st.open);
  const setPanelOpen = useRightPanel((st) => st.setOpen);
  const storedWidth = useRightPanel((st) => st.width);
  const setPanelWidth = useRightPanel((st) => st.setWidth);
  const dockState = useRightPanel((st) => st.dockState);
  const setDock = useRightPanel((st) => st.setDock);
  const dockShare = useRightPanel((st) => st.dockShare);
  const setDockShare = useRightPanel((st) => st.setDockShare);
  const panelRef = useRef<HTMLDivElement>(null);
  // Monotonic counter-color slot so consecutive groups stay visually distinct
  // even before a group's create lands in the cache.
  const colorSlotRef = useRef(-1);
  const fsToggleRef = useRef<(() => void) | null>(null);
  const [colorAvailable, setColorAvailable] = useState(false);
  const [colorEnabled, setColorEnabled] = useState(true);
  const [colorName, setColorName] = useState<string | undefined>(undefined);
  const [colorLoading, setColorLoading] = useState(false);
  const [colorUnavailable, setColorUnavailable] = useState(false);
  const [rotationDeg, setRotationDeg] = useState(0);
  // Last compare arrangement, remembered per user (see the effect below).
  const [lastLayout, setLastLayout] = useState<CompareLayout>(() => {
    try {
      const v = localStorage.getItem('viewer:compareLayout');
      if (v === 'horizontal' || v === 'vertical' || v === 'grid') return v;
    } catch {
      /* ignore */
    }
    return 'horizontal';
  });
  const colorUnsubRef = useRef<(() => void) | null>(null);

  const uids = useMemo(() => series?.map((s) => s.seriesInstanceUID) ?? [], [series]);
  const suffixes = useMemo(() => uids.map(uidSuffix), [uids]);
  // Compare shows pictures only: a document (a PDF) has its own view, no panes.
  const comparable = useMemo(() => comparableSeries(uids, (uid) => byUid.get(uid)?.kind), [uids, byUid]);
  const compareSuffixes = useMemo(() => comparable.map(uidSuffix), [comparable]);

  // Open on the image the address names (a share link's slide, or ?series=:
  // a shared link, an image card), else the first image in navigator order —
  // once the navigator has answered (openSlide.ts says why the wait).
  useEffect(() => {
    if (!series) return;
    if (selectedSeriesUID && series.some((s) => s.seriesInstanceUID === selectedSeriesUID)) return;
    const uid = initialSlide({ series, suffixes, slide, wanted: params.get('series'), navigatorSettled, ordered: images.length > 0 });
    if (uid) setSelectedSeriesUID(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, suffixes, selectedSeriesUID, navigatorSettled, images.length]);

  const selectedIndex = useMemo(
    () => uids.indexOf(selectedSeriesUID ?? ''),
    [uids, selectedSeriesUID],
  );
  const selectedSeries = selectedIndex >= 0 ? series?.[selectedIndex] : undefined;
  const compareIndex = useMemo(() => comparable.indexOf(selectedSeriesUID ?? ''), [comparable, selectedSeriesUID]);

  // How an image is named in the header and the compare panes: the
  // navigator's "5 of 7 · Name · Stain · Site, Side", numbered as the cards
  // are; for a series it does not know, the description, else its place in
  // the list.
  const seriesName = useCallback(
    (uid: string | undefined) => {
      const img = uid ? byUid.get(uid) : undefined;
      if (img) return displayName(img);
      const i = uids.indexOf(uid ?? '');
      return i >= 0 ? seriesDisplayName(series![i], i) : '';
    },
    [byUid, uids, series],
  );
  const slideLabel = seriesName(selectedSeriesUID);

  // ── Compare state, persisted in the URL (?compare&a&b&active&lock&layout) ──
  const patch = useCallback(
    (updates: Record<string, string | number | boolean | undefined>) => {
      setParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === undefined || v === false || v === '') n.delete(k);
            else n.set(k, String(v));
          }
          return n;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const compareMode = params.get('compare') === '1' && comparable.length >= 2;
  const layoutParam = params.get('layout');
  // How many panes the grid would show here — none for a study too small for
  // it (openSlide.ts says which). Resolved here rather than only in the pane
  // count, so a `layout=g` that cannot be honoured falls back to a pair.
  const gridPanes = gridPaneCount(comparable.length);
  const canGrid = gridPanes > 0;
  const requestedLayout: CompareLayout =
    layoutParam === 'v' ? 'vertical' : layoutParam === 'g' ? 'grid' : 'horizontal';
  const layout: CompareLayout =
    requestedLayout === 'grid' && !canGrid ? 'horizontal' : requestedLayout;
  // The arrangement fixes the pane count.
  const paneCount = layout === 'grid' ? gridPanes : 2;
  // Each pane's series — named in the address by `uidSuffix`, which a reorder
  // of the list leaves alone — defaulting to distinct slides: the one you were
  // looking at, then the next ones that are not already shown.
  const panes: (string | undefined)[] = resolvePanes(
    PANE_PARAMS.slice(0, paneCount).map((k) => params.get(k)),
    compareSuffixes,
    compareIndex,
  ).map((i) => comparable[i]);
  const activeParam = Number(params.get('active'));
  const activePane =
    Number.isInteger(activeParam) && activeParam > 0 && activeParam < paneCount ? activeParam : 0;
  const locked = params.get('lock') === '1';
  // Overlaying is a mode over the arrangement, not an arrangement of its own —
  // so un-stacking always returns to the split you were in.
  const stacked = params.get('stack') === '1';

  const layoutParamFor = (l: CompareLayout) =>
    l === 'vertical' ? 'v' : l === 'grid' ? 'g' : undefined;

  // The arrangement compare was last used in, so the `d` shortcut reopens compare
  // the way you had it. The URL is the source of truth while comparing; this
  // outlives it.
  // Remembers what was ASKED for, not what this study could honour: a two-series
  // study forces the fallback to horizontal, and persisting that would quietly
  // throw away a four-up preference just for having visited a small slide.
  useEffect(() => {
    if (!compareMode || requestedLayout === lastLayout) return;
    setLastLayout(requestedLayout);
    try {
      localStorage.setItem('viewer:compareLayout', requestedLayout);
    } catch {
      /* ignore */
    }
  }, [compareMode, requestedLayout, lastLayout]);

  const enterCompare = (l0: CompareLayout = lastLayout) => {
    const l: CompareLayout = l0 === 'grid' && !canGrid ? 'horizontal' : l0;
    const chosen = firstPanes(compareIndex, l === 'grid' ? gridPanes : 2, comparable.length);
    patch({
      compare: 1,
      ...Object.fromEntries(PANE_PARAMS.map((k, i) => [k, compareSuffixes[chosen[i]]])),
      active: undefined,
      lock: undefined,
      stack: undefined,
      layout: layoutParamFor(l),
    });
  };
  // Leave compare on the slide you were driving, not on whatever was selected
  // before compare opened: the active pane is the one you were looking at.
  const exitCompare = () => {
    const uid = panes[activePane];
    if (uid && uid !== selectedSeriesUID) {
      setSelectedSeriesUID(uid);
      setViewportKey((k) => k + 1);
    }
    patch({
      compare: undefined,
      ...Object.fromEntries(PANE_PARAMS.map((k) => [k, undefined])),
      active: undefined,
      lock: undefined,
      stack: undefined,
      layout: undefined,
    });
  };

  // A count belongs to its slide: leaving the slide — for another image, or
  // for another compare pane — saves and ends it, and a name field still
  // waiting for it is not opened on the next slide.
  const leaveSlide = () => {
    if (tool === 'counter') {
      setTool('none');
      void counter.end();
    }
    setNameRequest(null);
  };
  const selectPane = (p: number) => {
    if (p !== activePane) leaveSlide();
    patch({ active: p === 0 ? undefined : p });
  };

  const selectSeries = (uid: string) => {
    leaveSlide();
    if (compareMode) {
      // A slide already in a pane is selected there, not put in a second
      // one: one slide in two panes reads as two slides, and whatever is
      // drawn on it turns up in both.
      const shownIn = panes.indexOf(uid);
      if (shownIn >= 0) patch({ active: shownIn === 0 ? undefined : shownIn });
      else patch({ [PANE_PARAMS[activePane]]: uidSuffix(uid), lock: undefined });
    } else {
      setSelectedSeriesUID(uid);
      setViewportKey((k) => k + 1);
    }
  };

  // The series the right-hand panels reflect: the active pane in compare mode.
  const focusUID = compareMode ? panes[activePane] : selectedSeriesUID;
  // A compare pane's engine, once it draws the slide the pane shows.
  const paneEngine = (pane: number): ViewportEngine | null => {
    const p = paneEngines[pane];
    return p && p.series === panes[pane] ? p.engine : null;
  };
  // The engine of the image the panels reflect: the one an annotation picked
  // in the list is shown on, and whose pixel spacing measures it.
  const focusEngine = compareMode ? paneEngine(activePane) : engine;

  // The image on screen is kept in the address (?series=), so a reload stays
  // on it and a shared link opens on it. The entry is replaced, not pushed:
  // choosing an image is not a step Back should undo.
  useEffect(() => {
    if (!focusUID) return;
    if (onSlide) {
      onSlide(focusUID);
      return;
    }
    if (params.get('series') === focusUID) return;
    patch({ series: focusUID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusUID]);
  const { data: metadata, error: metaError } = useSeriesMetadata(studyUID, focusUID);
  // The slide this page is going to open on is asked for before the list
  // settles on it (expectedSlide: the one the address names, else the
  // navigator's first card). Opening a study was three round trips in a row
  // (the lists, then the slide's metadata, then its first tiles: 150 ms a
  // trip in production); its metadata now goes alongside the lists, and the
  // same query answers the page once it has picked the slide. Asked for until
  // a slide of this study's list is the selected one: the selection outlives
  // a change of study where the page is not remounted (enterprise). A named
  // slide the study turns out not to have costs one request, retried as any
  // failed one is, that the page then does nothing with.
  const expectedUID = expectedSlide({ wanted: params.get('series'), slide, images });
  useSeriesMetadata(studyUID, expectedUID, !(selectedSeriesUID && uids.includes(selectedSeriesUID)));

  // Reordering the navigator: for an admin or editor of the study's
  // workspace, as the navigator itself says. A refusal is reported the way an
  // annotation's is. A drag (or a keyboard move) puts the image next to its
  // neighbour, and the whole order shown then — every section and group, the
  // later arrivals held below — is saved as it is (useReorder); nothing is
  // sent for a drop where the image already is.
  const placeImages = useReorder(navigatorKey(studyUID, studyId), (message) => {
    if (notify) notify(message);
    else setNotice(message);
  });
  const reorder = useCallback(
    (imageId: string, target: Neighbours) => {
      const next = inDisplayOrder(reorderImages(images, imageId, target), specimen);
      if (next.some((img, i) => img.image_id !== images[i]?.image_id)) placeImages(next);
    },
    [images, specimen, placeImages],
  );
  // What this person has seen (decided 2026-09-25): an image is marked once
  // its viewer is up, in the single view or in any compare pane, where the
  // navigator says marks are kept (signed in; never a share link). Marked
  // once: an image already seen, or not the navigator's, sends nothing; one up
  // before the navigator answers is marked when it does (seen.ts).
  const seenTracked = navigator.data?.seen_tracked === true;
  const markSeen = useMarkSeen(navigatorKey(studyUID, studyId));
  const noteShown = useSeenMarker(byUid, seenTracked, markSeen, studyUID);
  // A document (a PDF) opens in its own view, not on the slide canvas: known
  // from the navigator's kind before anything loads, or from the file's SOP
  // class once its metadata is here (a row made before documents existed).
  const selectedImage = selectedSeries ? byUid.get(selectedSeries.seriesInstanceUID) : undefined;
  const documentOpen = !compareMode && !!selectedSeries
    && (selectedImage?.kind === 'document' || (!!metadata && sopClassOf(metadata) === SopClass.EncapsulatedPDF));
  // Its first page drawn: seen, as a slide is when its viewer is up.
  const [documentReady, setDocumentReady] = useState(false);
  // Its reader's controls, for the header.
  const [documentControls, setDocumentControls] = useState<DocumentControls | null>(null);
  useEffect(() => setDocumentReady(false), [selectedSeriesUID]);
  // After a reset, what is on screen is seen again at once (seen.ts).
  const onScreen = () => onScreenSeries(compareMode, panes, selectedSeries?.seriesInstanceUID, !!engine || documentReady);
  const onScreenRef = useRef(onScreen);
  onScreenRef.current = onScreen;
  const resetSeen = useResetSeen(
    navigatorKey(studyUID, studyId),
    (message) => {
      if (notify) notify(message);
      else setNotice(message);
    },
    () => onScreenRef.current().forEach(noteShown),
  );
  // An image's preview: by public id under the navigator's base; a series the
  // navigator does not know has only its DICOMweb thumbnail.
  const thumbnailOf = (img: NavigatorImage): string | null => {
    if (!isTail(img)) return imageThumbnail ? imageThumbnail(img.image_id) : `${base}/images/${encodeURIComponent(img.image_id)}/thumbnail`;
    return studyUID ? getDataSource().seriesThumbnailUrl(studyUID, img.series_instance_uid) : null;
  };

  // Annotations for the image the panels reflect: the single-mode slide, or
  // compare's selected pane (engine-agnostic; WSI only for now). Each slide's
  // own layer reads and writes its list too (SlideAnnotations). A refused
  // write has already been rolled back; the person is told why, and a 403
  // turns the tools off rather than letting the next shape vanish too —
  // unless it only says that one annotation is somebody else's.
  const onAnnotationWriteError = (e: Error) => {
    if (e instanceof AnnotationWriteError && e.status === 403 && e.code !== 'not_author') setDenied(true);
    if (notify) notify(e.message);
    else setNotice(e.message);
  };
  const { annotations, update, remove } = useAnnotations(studyId, focusUID, onAnnotationWriteError);
  const frameOfReferenceUID = getString(metadata?.[0], Tag.FrameOfReferenceUID);
  // Drawn on the single view's slide, and in compare on the panes side by
  // side, with the tools armed from the selected pane's engine. A stack has
  // none: it blends its panes, which would wash the shapes out with the
  // tissue, and which slide a shape drawn over it belongs to would be
  // anyone's guess.
  const annotationCaps = compareMode ? (stacked ? null : focusEngine?.capabilities) : caps;
  const supportsAnnotations = !!annotationCaps?.annotations && !!studyId;
  const access = annotationAccess({ supported: supportsAnnotations, readOnly });
  // No drawing over a document: its view has no canvas for a shape to sit on.
  const drawHere = access.draw && !documentOpen;

  // ── Counter sessions: a counting session lives here so the overlay (clicks),
  //    the panel (resume) and the toolbar (Done) share it. It saves
  //    as it goes. ──
  // A count's writes go to the list of the slide it is counted on, whatever
  // the panels show when they go out: a count ends when another pane is
  // pressed, and its last saves follow a create still on its way.
  const [countSeries, setCountSeries] = useState<string | undefined>(undefined);
  const countList = useAnnotations(studyId, countSeries ?? focusUID, onAnnotationWriteError);
  const counter = useCounterSession(studyId, {
    create: (a) => countList.create.mutateAsync(a).catch(() => undefined),
    update: (id, patch) => countList.update.mutate({ id, patch }),
    remove: (id) => countList.remove.mutate(id),
  });
  const session = counter.session;
  // A count just finished, for the overlay to open its name field on.
  const [nameRequest, setNameRequest] = useState<string | null>(null);
  // The annotation hovered, on the slide or on its row in the list: one hover,
  // shown in both places (user, 2026-10-10). Each side reports what its
  // pointer is on, and a side's "nothing" ends only a hover of its own: the
  // pointer leaving a slide annotation for a row reports the slide's nothing
  // and the row's something in one breath, and the slide's, landing last,
  // must not undo the row's. Each compare pane is a side of its own. Gone
  // with the slide the panel shows.
  const [hoveredAnnotation, setHoveredAnnotation] = useState<{ id: string; from: string } | null>(null);
  useEffect(() => setHoveredAnnotation(null), [focusUID]);
  const hoverFrom = (from: string) => (id: string | null) =>
    setHoveredAnnotation((h) => (id ? { id, from } : h?.from === from ? null : h));
  // What this person drew, which is what they may clear: an annotation is its author's.
  const ownAnnotations = annotations.filter((a) => a.isYou === true);
  // An annotation picked in the list is brought into view on the slide,
  // centred and zoomed to it, but not past 100% (user, 2026-10-10); on a phone
  // the details sheet makes way for it, as picking an image makes way for the
  // slide.
  const showAnnotation = (a: Annotation) => {
    if (!focusEngine || a.points.length === 0) return;
    const { minX, minY, maxX, maxY } = polygonBounds(a.points);
    focusEngine.showImageRegion({ minX, minY, maxX, maxY });
    setMobilePanel(null);
  };
  // The slide being worked on now, for a save that answers after it has changed.
  const shownSeriesRef = useRef(focusUID);
  shownSeriesRef.current = focusUID;

  // Disarm the tool when annotations aren't available (e.g. a flat-image
  // series or a compare stack) or may not be changed. A count in progress is
  // saved in the first case and dropped in the second, where saving it is
  // refused.
  useEffect(() => {
    if (supportsAnnotations) return;
    setTool('none');
    void counter.end();
  }, [supportsAnnotations]);
  useEffect(() => {
    if (access.edit) return;
    setTool('none');
    counter.discard();
  }, [access.edit]);
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 6000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const nextColorSlot = () => {
    const used = annotations
      .filter((a) => a.type === 'counter')
      .map((a) => counterColorIndex(a.style));
    const fromList = used.length ? Math.max(...used) : -1;
    const next = Math.max(fromList, colorSlotRef.current) + 1;
    colorSlotRef.current = next;
    return next;
  };

  const startNewGroup = () => {
    setCountSeries(focusUID);
    counter.start(nextColorSlot(), focusUID ?? null, frameOfReferenceUID ?? null);
    setTool('counter');
  };
  const activateCounter = (a: Annotation) => {
    setCountSeries(a.seriesInstanceUID ?? undefined);
    counter.resume(a);
    setTool('counter');
  };
  const startTool = (t: 'measure' | 'arrow' | 'rectangle' | 'ellipse' | 'polygon') => {
    if (tool === t) {
      exitTool();
    } else {
      void counter.end();
      setTool(t);
    }
  };
  // Leave the armed tool. Leaving the counter finishes the count, and its name
  // field opens once it is saved, as it does on a shape just drawn.
  const exitTool = () => {
    setTool('none');
    if (tool !== 'counter') return;
    const series = focusUID;
    void counter.end().then((id) => {
      if (id && shownSeriesRef.current === series) setNameRequest(id);
    });
  };
  // The toolbar's counter button switches counting on and off, as the other tools do.
  const toggleCounter = () => {
    if (tool === 'counter') exitTool();
    else startNewGroup();
  };
  // The shape currently armed (null if none) and the one the group button shows.
  const activeShape: ShapeKind | null =
    tool === 'rectangle'
      ? 'rectangle'
      : tool === 'ellipse'
        ? 'ellipse'
        : tool === 'polygon'
          ? polygonSmooth
            ? 'polygon-smooth'
            : 'polygon-straight'
          : null;
  const selectedShape = activeShape ?? lastShape;

  // Arm a shape from the dropdown; remembers the choice (persisted per user).
  const armShape = (kind: ShapeKind) => {
    setLastShape(kind);
    try {
      localStorage.setItem('viewer:lastShape', kind);
    } catch {
      /* ignore storage failures (private mode, etc.) */
    }
    void counter.end();
    if (kind === 'polygon-straight' || kind === 'polygon-smooth') {
      const smooth = kind === 'polygon-smooth';
      setPolygonSmooth(smooth);
      try {
        localStorage.setItem('viewer:polygonSmooth', smooth ? '1' : '0');
      } catch {
        /* ignore */
      }
      setTool('polygon');
    } else {
      setTool(kind);
    }
  };
  // Main shapes-group button: toggle the selected shape on/off.
  const toggleShapeGroup = () => {
    if (activeShape) exitTool();
    else armShape(selectedShape);
  };
  // An annotation being deleted that is being counted stops being counted.
  const dropCount = (id: string) => {
    if (session?.id !== id) return;
    counter.discard();
    setTool('none');
  };
  const deleteAnnotation = (id: string) => {
    dropCount(id);
    remove.mutate(id);
  };
  const deleteSession = () => {
    const id = counter.discard();
    if (id) countList.remove.mutate(id);
    setTool('none');
  };

  // The tools, as the single header and the compare bar both show them.
  const toolProps: AnnotationToolProps = {
    measureActive: tool === 'measure',
    onToggleMeasure: drawHere ? () => startTool('measure') : undefined,
    arrowActive: tool === 'arrow',
    onToggleArrow: drawHere ? () => startTool('arrow') : undefined,
    shapeSelected: selectedShape,
    shapeActive: activeShape,
    onToggleShape: drawHere ? toggleShapeGroup : undefined,
    onPickShape: drawHere ? armShape : undefined,
    counterActive: tool === 'counter',
    onToggleCounter: drawHere ? toggleCounter : undefined,
    onDoneCounting: exitTool,
    toolsDisabledReason: documentOpen ? undefined : access.disabledReason,
    onClearAnnotations: drawHere
      ? () => {
          counter.discard();
          if (tool === 'counter') setTool('none');
          ownAnnotations.forEach((a) => remove.mutate(a.id));
        }
      : undefined,
    annotationCount: ownAnnotations.length,
  };

  // One slide's annotations over its viewer: the single view's (pane null), or
  // a compare pane's. A tool armed draws on whichever pane is pressed — the
  // press selects it — except the counter, whose count belongs to the
  // selected pane's slide.
  const slideAnnotations = (on: ViewportEngine, series: string, pane: number | null) => {
    const selected = pane === null || pane === activePane;
    return (
      <SlideAnnotations
        // Not the bare UID: a compare pane's Viewport, its sibling, has that key.
        key={`annotations:${series}`}
        engine={on}
        studyInstanceUID={studyUID!}
        studyId={studyId!}
        seriesInstanceUID={series}
        tool={selected || tool !== 'counter' ? tool : 'none'}
        polygonSmooth={polygonSmooth}
        onWriteError={onAnnotationWriteError}
        onDeleting={dropCount}
        onExitTool={exitTool}
        session={selected ? session : null}
        onCounterAdd={counter.add}
        onCounterRemovePoint={counter.removeAt}
        onDeleteSession={deleteSession}
        nameRequest={nameRequest}
        onNameRequestTaken={() => setNameRequest(null)}
        readOnly={!access.edit}
        hoveredId={hoveredAnnotation?.id ?? null}
        onHover={hoverFrom(pane === null ? 'slide' : `slide:${pane}`)}
        active={selected}
      />
    );
  };

  // Surface the slide's ICC color toggle in the global bottom bar (single mode).
  useEffect(() => {
    if ((colorAvailable || colorUnavailable) && !compareMode) {
      useColorStore.getState().set({
        available: colorAvailable,
        enabled: colorEnabled,
        name: colorName,
        nameLoading: colorLoading,
        unavailable: colorUnavailable,
        toggle: () => engineRef.current?.setColorManaged(!engineRef.current.colorManaged().enabled),
      });
    } else {
      useColorStore.getState().clear();
    }
  }, [colorAvailable, colorEnabled, colorName, colorLoading, colorUnavailable, compareMode]);
  useEffect(
    () => () => {
      colorUnsubRef.current?.();
      useColorStore.getState().clear();
    },
    [],
  );

  // Keyboard shortcuts: Tab toggles compare; single keys arm annotation tools
  //   a arrow · m measure (measure) · r rectangle · e ellipse · p polygon · c counter
  // Each ignores repeats/modifiers and never fires while typing in a field.
  // Read through a ref, so a key acts on this render: the pane selected in
  // compare, the address as it is now. A dependency list never kept up — a
  // count started from the keyboard landed on the pane selected before, and
  // `d` left compare with the address naming another slide.
  const onKeyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  onKeyRef.current = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;

    // `b` folds the right panel to its rail and back. Wide layout only: on
    // a phone the panel is a sheet with its own button, and the fold is
    // remembered, so it must not be flipped where it cannot be seen.
    if (e.key.toLowerCase() === 'b') {
      e.preventDefault();
      if (!isLargeNow()) return;
      const panel = useRightPanel.getState();
      panel.setOpen(!panel.open);
      return;
    }

    // `d` toggles compare (dual view); needs ≥2 series to enter. Handled before
    // the annotation guard so it also works while comparing.
    if (e.key.toLowerCase() === 'd') {
      e.preventDefault();
      if (compareMode) exitCompare();
      // Not from a document: it has no compare (the user, 2026-10-09).
      else if (comparable.length >= 2 && !documentOpen) enterCompare();
      return;
    }

    // `[` / `]` rotate the view to the next 90° multiple CCW / CW (same as the
    // panel buttons). Read the live rotation off the engine.
    if ((e.key === '[' || e.key === ']') && caps?.rotate && !compareMode) {
      e.preventDefault();
      const cur = Math.round(engineRef.current?.getViewState()?.rotationDeg ?? 0);
      const next =
        e.key === '[' ? (Math.ceil(cur / 90) - 1) * 90 : (Math.floor(cur / 90) + 1) * 90;
      engineRef.current?.rotateTo(next);
      return;
    }

    // An X-ray's window/level, for anyone who may see it: `w` arms the drag
    // on the image, `i` inverts, Esc puts the drag down.
    if (windowLevel) {
      const key = e.key.toLowerCase();
      if (key === 'w') {
        e.preventDefault();
        toggleWindowArmed();
        return;
      }
      if (key === 'i') {
        e.preventDefault();
        windowLevel.invert();
        return;
      }
      if (e.key === 'Escape' && windowArmed) {
        setWindowArmed(false);
        return;
      }
    }

    // Tool shortcuts apply only where annotations are drawn — the single view,
    // compare's panes side by side — and only for somebody who may annotate.
    if (!access.draw) return;
    const toggle = (armed: boolean, arm: () => void) => {
      e.preventDefault();
      if (armed) exitTool();
      else arm();
    };
    switch (e.key.toLowerCase()) {
      case 'a':
        return toggle(tool === 'arrow', () => startTool('arrow'));
      case 'm':
        return toggle(tool === 'measure', () => startTool('measure'));
      case 'r':
        return toggle(tool === 'rectangle', () => armShape('rectangle'));
      case 'e':
        return toggle(tool === 'ellipse', () => armShape('ellipse'));
      case 'p':
        return toggle(tool === 'polygon' && !polygonSmooth, () => armShape('polygon-straight'));
      case 's':
        return toggle(tool === 'polygon' && polygonSmooth, () => armShape('polygon-smooth'));
      case 'c':
        return toggle(tool === 'counter', () => startNewGroup());
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => onKeyRef.current(e);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // The review scan: a slide's tool (slideTools), in single mode only.
  const reviewable = !!caps?.magnification && slideTools(caps);
  const reviewScan = useReviewScan({
    engine,
    supported: reviewable && !compareMode,
    tool,
    seriesInstanceUID: selectedSeriesUID,
    // What the scan covers is kept per person and image (decided 2026-10-01),
    // under the same rule as the seen marks: signed in, never a share link.
    seenImageId: seenTracked && selectedSeriesUID ? (byUid.get(selectedSeriesUID)?.image_id ?? null) : null,
  });

  // The snapshot: a single slide whose engine hands out its tiles. Arming the
  // camera leaves the tool; arming a tool puts the camera down.
  const capture = useCapture({
    engine,
    supported: !compareMode && !documentOpen,
    onArm: exitTool,
  });
  const disarmCapture = capture.disarm;
  useEffect(() => {
    if (tool !== 'none') disarmCapture();
  }, [tool, disarmCapture]);

  // An X-ray's window/level: the box top right, which shows the window and
  // takes it by its middle or its edges (WindowBox); the toolbar's tool, which
  // drags it on the image (W); and Invert (I). In compare each X-ray pane has
  // its own (user, 2026-10-16): its box over it, the drag on whichever is
  // pressed, and the toolbar's Invert on the selected pane — the driven one
  // when stacked and locked, as the stack's controls are. One tool armed at a
  // time: arming it leaves the annotation tool and the snapshot, and either of
  // those puts it down.
  const paneWindowEngines = compareMode ? panes.map((_, i) => paneEngine(i)) : [];
  const windowLevels = useWindowLevels(compareMode ? paneWindowEngines : [engine]);
  const windowLevel = windowLevels[compareMode ? (stacked && locked ? 0 : activePane) : 0] ?? null;
  const [windowArmed, setWindowArmed] = useState(false);
  const toggleWindowArmed = () => {
    if (windowArmed) return setWindowArmed(false);
    exitTool();
    disarmCapture();
    setWindowArmed(true);
  };
  const windowable = !!windowLevel;
  useEffect(() => {
    if (tool !== 'none' || capture.armed || !windowable) setWindowArmed(false);
  }, [tool, capture.armed, windowable]);
  const windowControl = windowLevel ? (
    <WindowControl armed={windowArmed} onArm={toggleWindowArmed} inverted={windowLevel.inverted} onInvert={windowLevel.invert} />
  ) : null;

  // The right-hand tabs: Annotations, Information, then the shell's. A tab
  // that is gone (a shell that stopped passing it) falls back to Annotations.
  // The tabs in their order: Annotations, the shell's (AI), Info — and on a
  // phone the dock as one more, since there is no room under the tabs there.
  const gatedTabs = (extraTabs ?? []).filter((t): t is StudyPageTab & { useVisible: NonNullable<StudyPageTab['useVisible']> } => !!t.useVisible);
  const shownExtra = (extraTabs ?? []).filter((t) => !t.useVisible || tabVisible[t.id] === true);
  // A document has nothing to annotate: no Annotations tab while one is open.
  const tabs: PanelTab[] = [
    ...(documentOpen ? [] : [{ id: 'annotations', label: 'Annotations', title: 'Annotations', icon: <PenLine className="h-4 w-4" aria-hidden /> }]),
    ...shownExtra.map((t) => ({ id: t.id, label: t.label, title: t.title ?? t.id, icon: t.icon ?? <span className="text-[10px] font-semibold uppercase">{(t.title ?? t.id).slice(0, 2)}</span> })),
    { id: 'information', label: 'Info', title: 'Information', icon: <Info className="h-4 w-4" aria-hidden /> },
    ...(dock && !large ? [{ id: dock.id, label: dock.label, title: dock.label, icon: dock.icon }] : []),
  ];
  const shownTab = tabs.some((t) => t.id === rightTab) ? rightTab : tabs[0].id;
  const activeExtra = shownExtra.find((t) => t.id === shownTab);
  const dockAsTab = !!dock && !large && shownTab === dock.id;
  const dockDocked = !!dock && large;
  const focusImage = focusUID ? byUid.get(focusUID) : undefined;
  const tabContext: StudyTabContext = {
    // Only an engine drawing the image the panels reflect: in compare the
    // single view's is gone, and while a slide is swapped it is the old one's.
    engine: !compareMode && engine && engineSeries === focusUID ? engine : null,
    noEngineReason: compareMode ? 'Masks show when the image is open on its own, not in compare.' : undefined,
    studyUID,
    studyId,
    seriesUID: focusUID,
    imageId: focusImage && !isTail(focusImage) ? focusImage.image_id : undefined,
    imageKind: focusImage?.kind,
    readOnly,
  };

  // Surface the patient in the global top bar while this study is open.
  const setPatient = useHeaderStore((s) => s.setPatient);
  const clearPatient = useHeaderStore((s) => s.clear);
  useEffect(() => {
    const ds = metadata?.[0];
    if (!ds) return;
    setPatient({
      patientName: formatPersonName(getPersonName(ds, Tag.PatientName)),
      patientId: getString(ds, Tag.PatientID),
      patientSex: getString(ds, Tag.PatientSex),
      patientAge: patientAge(ds),
      patientBirthDate: formatDicomDate(getString(ds, Tag.PatientBirthDate)),
      patientIdIssuer: getString(ds, Tag.IssuerOfPatientID),
      referringPhysician: formatPersonName(getPersonName(ds, Tag.ReferringPhysicianName)),
      accession: getString(ds, Tag.AccessionNumber),
      studyDateTime: [
        formatDicomDate(getString(ds, Tag.StudyDate)),
        formatDicomTimeShort(getString(ds, Tag.StudyTime)),
      ]
        .filter(Boolean)
        .join(' '),
    });
  }, [metadata, setPatient]);
  useEffect(() => () => clearPatient(), [clearPatient]);

  if (seriesError) {
    // The bars stay: a study that fails to load still needs its way back.
    return (
      <div className="flex h-full flex-col">
        {topBar}
        <div className="min-h-0 flex-1">
          <ErrorBox title="Failed to load study" message={(seriesError as Error).message} />
        </div>
        {bottomBar}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {topBar}
      {gatedTabs.map((t) => <TabVisibility key={t.id} tab={t} ctx={tabContext} onChange={onTabVisible} />)}
      {/* Phones: the image list and the side panel open over the image. */}
      <div className="flex shrink-0 gap-2 border-b px-3 py-2 lg:hidden">
        <button
          type="button"
          onClick={() => setMobilePanel(mobilePanel === 'images' ? null : 'images')}
          className={'rounded-md border px-3 py-1 text-sm ' + (mobilePanel === 'images' ? 'bg-accent text-foreground' : 'text-muted-foreground')}
        >
          Images{series?.length ? ` (${series.length})` : ''}
        </button>
        <button
          type="button"
          onClick={() => setMobilePanel(mobilePanel === 'details' ? null : 'details')}
          className={'rounded-md border px-3 py-1 text-sm ' + (mobilePanel === 'details' ? 'bg-accent text-foreground' : 'text-muted-foreground')}
        >
          Details
        </button>
      </div>
      {/* overflow-hidden: this page fills the window and never scrolls — the two
          side panels scroll inside themselves. Without it the row's width leaks
          into the document, and a child half a pixel too wide (the right
          panel's three tabs, whose labels don't quite fit their third of it)
          gives the whole page a horizontal scrollbar. */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
      {notice && (
        <div
          role="alert"
          className="absolute left-1/2 top-3 z-40 flex max-w-[90%] -translate-x-1/2 items-center gap-3 rounded-md border border-destructive/50 bg-background px-3 py-2 text-sm text-destructive shadow-md"
        >
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-muted-foreground hover:text-foreground">
            Dismiss
          </button>
        </div>
      )}
      {/* Left: the image navigator. It scrolls inside itself in both layouts
          (its own sticky bars need it to be the scroller), so the phone
          wrapper only positions it. Drawn once the navigator has answered,
          like the slide itself: from QIDO alone every image would sit under
          "Other" for a moment and then jump into its section. */}
      <div className={(mobilePanel === 'images' ? 'absolute inset-0 z-30 bg-background' : 'hidden') + ' lg:static lg:z-auto lg:block lg:w-60 lg:shrink-0 lg:border-r lg:bg-transparent'}>
        {seriesLoading || !navigatorSettled ? (
          <Spinner className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <ImageNavigator
            // One per study: what it holds for a viewing (Collapse, the section on screen, a reset's question) starts afresh.
            key={studyUID}
            images={shownImages}
            specimen={specimen}
            selectedUID={focusUID}
            assignment={
              compareMode
                ? (uid) => {
                    const i = panes.indexOf(uid);
                    return i >= 0 ? PANE_LABELS[i] : undefined;
                  }
                : undefined
            }
            thumbnail={thumbnailOf}
            onReorder={navigator.data?.editable ? reorder : undefined}
            onResetSeen={seenTracked && studyId ? () => resetSeen(studyId) : undefined}
            onSelect={(uid: string) => {
              selectSeries(uid);
              setMobilePanel(null);
            }}
          />
        )}
      </div>

      {/* Center: compare panes, or single viewport */}
      {compareMode ? (
        <CompareView
          studyInstanceUID={studyUID!}
          paneSeries={panes}
          active={activePane}
          onActivate={selectPane}
          locked={locked}
          onToggleLock={() => patch({ lock: locked ? undefined : 1 })}
          layout={layout}
          onSetLayout={(l) => patch({ layout: layoutParamFor(l), active: undefined })}
          stacked={stacked}
          onToggleStack={() => patch({ stack: stacked ? undefined : 1 })}
          canGrid={canGrid}
          onExit={exitCompare}
          seriesName={seriesName}
          onShown={noteShown}
          onPaneEngine={onPaneEngine}
          tools={toolProps}
          paneAnnotations={(i) => {
            const on = paneEngine(i);
            const uid = panes[i];
            return on && uid && studyId && on.capabilities.annotations ? slideAnnotations(on, uid, i) : null;
          }}
          paneWindows={paneWindowEngines.map((e, i) => {
            const level = windowLevels[i];
            return e && level ? { level, engine: e } : null;
          })}
          windowArmed={windowArmed}
          windowControl={windowControl}
        />
      ) : (
        <div className="flex min-w-0 flex-1 flex-col">
          <StudyHeader
            metadata={metadata}
            slideLabel={slideLabel}
            seriesUID={selectedSeriesUID}
            rotationDeg={rotationDeg}
            onRotate={caps?.rotate ? (deg) => engineRef.current?.rotateTo(deg) : undefined}
            onFullscreen={() => fsToggleRef.current?.()}
            onCompare={!documentOpen && comparable.length >= 2 ? enterCompare : undefined}
            canGrid={canGrid}
            {...toolProps}
            onEditDetails={editDetails}
            onToggleReviewScan={reviewable ? reviewScan.toggle : undefined}
            reviewScanActive={reviewScan.open}
            reviewScanLoading={reviewScan.loading}
            onPrefetchReviewScan={reviewScan.prefetch}
            document={documentOpen ? documentControls : null}
            onToggleCapture={capture.supported ? capture.toggle : undefined}
            captureActive={capture.armed}
            windowControl={windowControl}
          />
          <div className="relative min-h-0 flex-1 bg-viewport">
            {metaError ? (
              <ErrorBox title="Failed to load series metadata" message={(metaError as Error).message} />
            ) : selectedSeries ? (
              // Mounted before the metadata arrives: the backdrop needs only the
              // UIDs, so the slide's picture is up while the metadata is fetched.
              // Only for a slide IN this study's list: when the page is reused
              // for another study the old selection lingers for a render, and a
              // Viewport kept across that would carry the old slide's state (an
              // error it can never clear) into the new one. Unmounted here, it
              // starts fresh once the new study's slide is chosen.
              documentOpen ? (
                // Kept across a switch from one document to another: the frame
                // opens the next one itself (a frame put in place of another
                // is drawn and not shown, in Chrome).
                <DocumentView
                  key="document"
                  url={selectedImage && !isTail(selectedImage) ? `${base}/images/${encodeURIComponent(selectedImage.image_id)}/document` : null}
                  name={selectedImage?.name ?? null}
                  onReady={() => {
                    noteShown(selectedSeries.seriesInstanceUID);
                    setDocumentReady(true);
                  }}
                  onFullscreenToggleReady={(t) => {
                    fsToggleRef.current = t;
                  }}
                  onControls={setDocumentControls}
                />
              ) : (
              <>
              <Viewport
                key={viewportKey}
                studyInstanceUID={studyUID!}
                seriesInstanceUID={selectedSeries.seriesInstanceUID}
                metadata={metadata}
                inverted={windowLevel?.inverted ?? false}
                onEngineReady={(e) => {
                  if (e) noteShown(selectedSeries.seriesInstanceUID);
                  engineRef.current = e;
                  setEngine(e);
                  setEngineSeries(e ? selectedSeries.seriesInstanceUID : undefined);
                  setCaps(e ? e.capabilities : null);
                  colorUnsubRef.current?.();
                  colorUnsubRef.current = null;
                  const onView = (s?: EngineViewState) => {
                    const ci = engineRef.current?.colorManaged();
                    setColorAvailable(!!ci?.available);
                    setColorEnabled(ci?.enabled ?? true);
                    setColorName(ci?.name);
                    setColorLoading(!!ci?.nameLoading);
                    setColorUnavailable(!!ci?.unavailable);
                    const vs = s ?? engineRef.current?.getViewState() ?? null;
                    if (vs) setRotationDeg(((Math.round(vs.rotationDeg) % 360) + 360) % 360);
                  };
                  onView();
                  if (e) colorUnsubRef.current = e.subscribe(onView);
                }}
                onFullscreenToggleReady={(t) => {
                  fsToggleRef.current = t;
                }}
                scan={reviewScan.session}
                scanPending={reviewScan.pending}
                capture={capture.armed ? { onCapture: capture.capture } : null}
              />
              {capture.request && engine && (
                <Suspense fallback={null}>
                  <CaptureDialog
                    engine={engine}
                    rect={capture.request}
                    thumbnailUrl={getDataSource().seriesThumbnailUrl(studyUID!, selectedSeries.seriesInstanceUID)}
                    fileParts={{
                      id: deriveSeriesInfo(metadata ?? []).slideId,
                      name: selectedImage?.name,
                      stain: selectedImage?.stain?.meaning,
                    }}
                    onClose={capture.close}
                  />
                </Suspense>
              )}
              {engine && supportsAnnotations && slideAnnotations(engine, selectedSeries.seriesInstanceUID, null)}
              {engine && windowLevel && <WindowOverlay level={windowLevel} engine={engine} armed={windowArmed} />}
              </>
              )
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      )}

      {/* Right: tabbed Annotations / the shell's tabs / Info, the slide's label
          and the fold button at the strip's end, and under them the shell's
          dock (the Discussion) at its share of the height; a handle on the
          left edge for the width. Folded, a rail of the same entries. On a
          phone it is the "Details" sheet over the image: the tabs, the dock
          as one more of them, nothing to fold or drag. */}
      <div
        ref={panelRef}
        className={(mobilePanel === 'details' ? 'absolute inset-0 z-30 flex flex-col bg-background' : 'hidden') + ' lg:relative lg:z-auto lg:flex lg:max-w-[50vw] lg:shrink-0 lg:flex-col lg:border-l lg:bg-transparent'}
        style={large ? { width: panelOpen ? storedWidth : RAIL_WIDTH } : undefined}
      >
        {large && !panelOpen ? (
          <PanelRail
            tabs={tabs}
            activeTab={shownTab}
            dock={dock}
            dockCount={dock?.Count ? <dock.Count ctx={tabContext} /> : null}
            onTab={(id) => {
              setRightTab(id);
              setPanelOpen(true);
              // A dock across the whole panel would hide the tab just chosen.
              if (dockState === 'max') setDock('open');
            }}
            onDock={() => {
              setPanelOpen(true);
              if (dockState === 'collapsed') setDock('open');
            }}
            onOpen={() => setPanelOpen(true)}
          />
        ) : (
          <>
            {!(dockDocked && dockState === 'max') && (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex shrink-0 items-center gap-1 border-b pr-1">
                  {tabs.map((t) => (
                    <TabButton key={t.id} active={shownTab === t.id} crowded={tabs.length > 3} onClick={() => setRightTab(t.id)}>
                      {t.label}
                    </TabButton>
                  ))}
                  <LabelImage studyInstanceUID={studyUID} seriesInstanceUID={focusUID} />
                  {large && (
                    <PanelIconButton label="Hide panel (b)" side="bottom" onClick={() => setPanelOpen(false)}>
                      <ChevronsRight className="h-4 w-4" aria-hidden />
                    </PanelIconButton>
                  )}
                </div>
                <div className="min-h-0 flex-1 overflow-auto">
                  {dockAsTab && dock ? (
                    dock.render(tabContext)
                  ) : activeExtra ? (
                    activeExtra.render(tabContext)
                  ) : shownTab === 'annotations' ? (
                    <AnnotationsPanel
                      annotations={annotations}
                      session={session}
                      pixelSpacingMm={focusEngine ? focusEngine.pixelSpacingMm() : null}
                      onDelete={deleteAnnotation}
                      onActivateCounter={activateCounter}
                      onShow={showAnnotation}
                      onRename={(id, name) => update.mutate({ id, patch: { text: name } })}
                      readOnly={!access.edit}
                      hoveredId={hoveredAnnotation?.id ?? null}
                      onHover={hoverFrom('list')}
                    />
                  ) : metadata ? (
                    <MetadataPanel metadata={metadata} />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      No metadata.
                    </div>
                  )}
                </div>
              </div>
            )}
            {dockDocked && dock && (
              <section
                aria-label={dock.label}
                className={cn('flex min-h-0 flex-col border-t', dockState === 'max' && 'flex-1')}
                style={dockState === 'open' ? { flex: `0 0 ${Math.round(dockShare * 1000) / 10}%` } : dockState === 'collapsed' ? { flex: 'none' } : undefined}
              >
                {dockState === 'open' && <DockDivider panelRef={panelRef} onShare={setDockShare} onDouble={() => setDock('max')} />}
                <div className="flex h-9 shrink-0 items-center gap-1 pl-2 pr-1 text-sm font-medium">
                  <button
                    type="button"
                    className="flex h-full min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => setDock(dockState === 'collapsed' ? 'open' : 'collapsed')}
                    aria-expanded={dockState !== 'collapsed'}
                  >
                    <span className="text-muted-foreground">{dock.icon}</span>
                    <span className="truncate">{dock.label}</span>
                    {dock.Count ? (
                      <span className="text-xs tabular-nums text-muted-foreground empty:hidden">
                        <dock.Count ctx={tabContext} />
                      </span>
                    ) : null}
                  </button>
                  {dockState !== 'collapsed' && (
                    <PanelIconButton label={dockState === 'max' ? 'Back to its share' : 'Whole panel'} side="left" onClick={() => setDock(dockState === 'max' ? 'open' : 'max')}>
                      {dockState === 'max' ? <Minimize2 className="h-3.5 w-3.5" aria-hidden /> : <Maximize2 className="h-3.5 w-3.5" aria-hidden />}
                    </PanelIconButton>
                  )}
                  <PanelIconButton label={dockState === 'collapsed' ? 'Show' : 'Fold'} side="left" onClick={() => setDock(dockState === 'collapsed' ? 'open' : 'collapsed')}>
                    {dockState === 'collapsed' ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
                  </PanelIconButton>
                </div>
                {dockState !== 'collapsed' && <div className="min-h-0 flex-1 overflow-hidden">{dock.render(tabContext)}</div>}
              </section>
            )}
            {large && <WidthHandle panelRef={panelRef} onWidth={setPanelWidth} onDouble={() => setPanelWidth(PANEL_DEFAULT_WIDTH)} />}
          </>
        )}
      </div>
      </div>
      {bottomBar}
    </div>
  );
}

/** A tab as the strip and the rail draw it. */
interface PanelTab {
  id: string;
  label: ReactNode;
  title: string;
  icon: ReactNode;
}

/** The folded panel: one icon per tab, then the dock's, each opening the panel on it. */
function PanelRail({
  tabs,
  activeTab,
  dock,
  dockCount,
  onTab,
  onDock,
  onOpen,
}: {
  tabs: PanelTab[];
  activeTab: string;
  dock: StudyPageDock | undefined;
  dockCount: ReactNode;
  onTab: (id: string) => void;
  onDock: () => void;
  onOpen: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center gap-1 py-1.5">
      <PanelIconButton label="Show panel (b)" side="left" onClick={onOpen}>
        <ChevronsLeft className="h-4 w-4" aria-hidden />
      </PanelIconButton>
      <div className="my-1 h-px w-5 bg-border" />
      {tabs.map((t) => (
        <PanelIconButton key={t.id} label={t.title} side="left" active={activeTab === t.id} onClick={() => onTab(t.id)}>
          {t.icon}
        </PanelIconButton>
      ))}
      {dock ? (
        <>
          <div className="my-1 h-px w-5 bg-border" />
          <PanelIconButton label={dock.label} side="left" badge={dockCount} onClick={onDock}>
            {dock.icon}
          </PanelIconButton>
        </>
      ) : null}
    </div>
  );
}

/** The panel's small square buttons (fold, dock controls, the rail), each with the app's tooltip. */
function PanelIconButton({
  label,
  side,
  active = false,
  badge,
  onClick,
  children,
}: {
  label: string;
  side: 'left' | 'bottom';
  active?: boolean;
  /** Drawn as a pill at the corner when it renders something. */
  badge?: ReactNode;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className={cn(
            'relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
            active && 'bg-accent text-foreground',
          )}
        >
          {children}
          {badge ? (
            <span className="absolute -right-0.5 -top-0.5 min-w-3.5 rounded-full bg-primary px-1 text-center text-[9px] font-semibold leading-3.5 text-primary-foreground empty:hidden">
              {badge}
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side={side}>{label}</TooltipContent>
    </Tooltip>
  );
}

/** Runs one drag: the moves until the pointer lifts, with the cursor and selection held for the duration. */
function drag(e: ReactPointerEvent<HTMLElement>, cursor: string, onMove: (ev: PointerEvent) => void, onEnd: (ev: PointerEvent) => void): void {
  e.preventDefault();
  const body = document.body;
  const before = { cursor: body.style.cursor, select: body.style.userSelect };
  body.style.cursor = cursor;
  body.style.userSelect = 'none';
  const up = (ev: PointerEvent) => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
    body.style.cursor = before.cursor;
    body.style.userSelect = before.select;
    onEnd(ev);
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
}

/** The panel's left edge: drag for the width, double-click for the default. */
function WidthHandle({ panelRef, onWidth, onDouble }: { panelRef: RefObject<HTMLDivElement>; onWidth: (w: number, remember?: boolean) => void; onDouble: () => void }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the panel"
      onDoubleClick={onDouble}
      onPointerDown={(e) => {
        const panel = panelRef.current;
        if (!panel) return;
        // From the width drawn, not the one remembered: a narrower window
        // than last time caps the panel at half of itself (max-w-[50vw]).
        const startWidth = panel.getBoundingClientRect().width;
        const startX = e.clientX;
        const at = (ev: PointerEvent) => clampPanelWidth(startWidth + (startX - ev.clientX), window.innerWidth);
        drag(
          e,
          'col-resize',
          (ev) => onWidth(at(ev), false),
          (ev) => onWidth(at(ev), true),
        );
      }}
      // A 10 px target astride the border, drawing a 2 px line under the cursor.
      className="group absolute inset-y-0 left-0 z-10 w-2.5 -translate-x-1/2 cursor-col-resize"
    >
      <div className="mx-auto h-full w-0.5 transition-colors group-hover:bg-foreground/25 group-active:bg-foreground/40" />
    </div>
  );
}

/** The dock's top edge: drag for its share of the panel, double-click for the whole panel. */
function DockDivider({ panelRef, onShare, onDouble }: { panelRef: RefObject<HTMLDivElement>; onShare: (share: number, remember?: boolean) => void; onDouble: () => void }) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize the dock"
      onDoubleClick={onDouble}
      onPointerDown={(e) => {
        const panel = panelRef.current;
        if (!panel) return;
        const rect = panel.getBoundingClientRect();
        const at = (ev: PointerEvent) => clampDockShare((rect.bottom - ev.clientY) / rect.height);
        drag(
          e,
          'row-resize',
          (ev) => onShare(at(ev), false),
          (ev) => onShare(at(ev), true),
        );
      }}
      // A 10 px target astride the dock's top border, drawing a 2 px line under the cursor.
      className="group relative z-10 -mt-1.5 -mb-1 h-2.5 shrink-0 cursor-row-resize"
    >
      <div className="mx-auto mt-1 h-0.5 w-full transition-colors group-hover:bg-foreground/25 group-active:bg-foreground/40" />
    </div>
  );
}

function TabButton({
  active,
  crowded = false,
  onClick,
  children,
}: {
  active: boolean;
  /** More than three tabs share the panel: narrower padding, so every label still fits. */
  crowded?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        // min-w-0 so the tabs really do share the panel: left at the default
        // min-width they sit at their labels' widths instead, and three of
        // them are wider than it. flex-auto shares it by label, so a short
        // one ("AI") takes little and the long ones keep their words; the
        // padding keeps every label inside its share; truncate is what a
        // longer one gets in future, rather than silently spilling out of the
        // panel again.
        'min-w-0 flex-auto truncate border-b-2 py-2 text-sm font-medium transition-colors ' +
        (crowded ? 'px-1.5 ' : 'px-2 ') +
        (active
          ? 'border-foreground text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}
